<template>
  <div>
    <b-modal
      id="visa-info-modal"
      ref="visa-info-modal-ref"
      hide-footer
      hide-header
      title="Visa Information"
    >
      <div class="entry-reqs-content p-0">
        <div class="entry-reqs-header">
          <h5 class="entry-reqs-title">
            Entry Requirements for booking:
            <strong style="text-transform: uppercase">{{
              getBookingNumber
            }}</strong>
          </h5>
          <button
            @click="closeModal"
            type="button"
            class="close"
            aria-label="Close"
          >
            <span aria-hidden="true"
              ><i class="material-icons">&#xE5CD;</i></span
            >
          </button>
        </div>
        <div v-if="hasEntryRequirements" class="entry-reqs-body">
          <p>
            Below is a table outlining the requirements needed to enter the
            countries for your booking. To find out more information, please
            click on the link for each requirement.
          </p>
          <p>
            Trailfinders offers a service for selected visas. If any visas on
            your booking appear as part of our service, this will be indicated
            in the table. To book an appointment with our visa team, please
            follow the link found in the visa information below.
          </p>
        </div>

        <div v-if="hasEntryRequirements">
          <!-- Mandatory Requirements table: DESKTOP ONLY -->
          <table class="entry-reqs-table">
            <tr class="title-row">
              <th colspan="4">Your Mandatory Entry Requirements</th>
            </tr>

            <tr class="col-header">
              <td></td>
              <td>Visas</td>
              <td>Online Entry Forms</td>
              <td>Vaccinations</td>
            </tr>

            <!-- Travellers -->
            <tr
              v-for="client in visaContent.entryRequirements"
              :key="client.cl_id"
            >
              <td class="traveller-name">
                <div class="name">{{ client.cl_name }}</div>
                <div class="passport">{{ client.cl_passport }}</div>
              </td>
              <!-- Visas required -->
              <td class="visa-cell">
                <template v-if="client.visaCountries.length > 0">
                  <div
                    v-for="visaCountry in client.visaCountries"
                    :key="visaCountry.country"
                  >
                    <div class="visa-cell-item-container">
                      <a
                        class="visa-cell-item"
                        :class="{ 'visa-cell-item-disabled': isHomeCountry(visaCountry.code) }"
                        :href="
                          isHomeCountry(visaCountry.code)
                            ? null
                            : getFcoAdviceUrl(visaCountry)
                        "
                        target="_blank"
                        @click="isHomeCountry(visaCountry.code) && $event.preventDefault()"
                        >{{ visaCountry.description }}
                        <i v-if="!isHomeCountry(visaCountry.code)" class="material-icons">open_in_new</i></a
                      >
                      <a
                        v-if="displayTfVisaServiceLink(visaCountry.code)"
                        class="tf-visa-service"
                        :href="getTfLink(visaCountry.code)"
                        target="_blank"
                        >TF Visa Service
                        <i class="material-icons">arrow_forward</i></a
                      >
                    </div>
                  </div>
                </template>
                <template v-else><p>None required</p></template>
              </td>
              <!-- Online Entry Forms -->
              <td class="online-entry-cell">
                <div
                  v-if="client.preTravelForms.length > 0"
                  class="online-entry-cell-link-container"
                >
                  <a
                    class="online-entry-cell-link"
                    v-for="form in client.preTravelForms"
                    :key="form.country"
                    @click="scrollToOnlineEntryForms"
                  >
                    {{ form.country }}<i class="material-icons">open_in_new</i>
                  </a>
                </div>
                <div v-else>
                  <span> None required </span>
                </div>
              </td>
              <!-- Vaccines -->
              <td class="vacc-cell">
                <div v-if="aggregatedVaccines[client.cl_id]">
                  <div
                    class="vacc-item"
                    v-for="(countries, label) in aggregatedVaccines[
                      client.cl_id
                    ].vaccines"
                    :key="label"
                  >
                    <a
                      class="vacc-cell-link"
                      href="https://www.travelhealthpro.org.uk/countries"
                      target="_blank"
                      >{{ label }}</a
                    >
                    <div class="vacc-type">
                      Mandatory certificate for:
                      <span class="vacc-countries">{{
                        countries.join(', ')
                      }}</span>
                    </div>
                  </div>
                  <div>
                    <span class="vacc-cell-link-container">
                      Read
                      <a
                        class="vacc-cell-travel-health"
                        @click="openTravelHealthModal"
                        >Travel Health
                        <i class="material-icons">arrow_forward</i></a
                      >
                    </span>
                  </div>
                </div>
                <div v-else>
                  <span>
                    No mandatory vaccinations. Read
                    <a
                      class="vacc-cell-travel-health"
                      @click="openTravelHealthModal"
                      >Travel Health
                      <i class="material-icons">arrow_forward</i></a
                    >
                  </span>
                </div>
              </td>
            </tr>
          </table>
          <!-- Mandatory Requirements accordions: MOBILE ONLY -->
          <div class="passenger-accordions">
            <div class="title-row">
              <p>Your Mandatory Entry Requirements</p>
            </div>
            <div
              v-for="client in visaContent.entryRequirements"
              :key="client.cl_id"
            >
              <PassengerAccordion
                :title="client.cl_name"
                :passport="client.cl_passport"
                :isOpen="openPassengerIndex === client.cl_id"
                @toggle="togglePassengerAccordion(client.cl_id)"
              >
                <!-- VISAS -->
                <div class="passenger-accordion-visas">
                  <p class="passenger-accordion-visas-header">Visas</p>
                  <div v-if="client.visaCountries.length > 0">
                    <div
                      v-for="visaCountry in client.visaCountries"
                      :key="visaCountry.country"
                    >
                      <div class="">
                        <a
                          class="passenger-visa"
                          :class="{ 'visa-cell-item-disabled': isHomeCountry(visaCountry.code) }"
                          :href="
                            isHomeCountry(visaCountry.code)
                              ? null
                              : getFcoAdviceUrl(visaCountry)
                          "
                          target="_blank"
                          @click="isHomeCountry(visaCountry.code) && $event.preventDefault()"
                          >{{ visaCountry.description }}
                          <i class="material-icons">open_in_new</i></a
                        >
                        <a
                          v-if="displayTfVisaServiceLink(visaCountry.code)"
                          class="tf-visa-service"
                          :href="getTfLink(visaCountry.code)"
                          target="_blank"
                          >TF Visa Service
                          <i class="material-icons">arrow_forward</i></a
                        >
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <p>None required</p>
                  </div>
                </div>

                <!-- ONLINE ENTRY FORMS -->
                <div class="passenger-accordion-forms-container">
                  <p class="passenger-accordion-forms-header">
                    Online Entry Forms
                  </p>

                  <p />
                  <div class="passenger-accordion-forms">
                    <div v-if="client.preTravelForms.length > 0" class="">
                      <a
                        class="passenger-online-form"
                        v-for="form in client.preTravelForms"
                        :key="form.country"
                        @click="scrollToOnlineEntryForms"
                      >
                        {{ form.country
                        }}<i class="material-icons">open_in_new</i>
                      </a>
                    </div>
                    <div v-else>
                      <span> None required </span>
                    </div>
                  </div>
                </div>

                <!-- VACCINES -->
                <div class="passenger-accordion-vaccines">
                  <p class="passenger-accordion-vaccines-header">Vaccines</p>
                  <div class="vacc-cell">
                    <div v-if="aggregatedVaccines[client.cl_id]">
                      <div
                        class="vacc-item"
                        v-for="(countries, label) in aggregatedVaccines[
                          client.cl_id
                        ].vaccines"
                        :key="label"
                      >
                        <a
                          class="vacc-cell-link"
                          href="https://www.travelhealthpro.org.uk/countries"
                          target="_blank"
                          >{{ label }}</a
                        >
                        <div class="vacc-type">
                          Mandatory certificate for:
                          <span class="vacc-countries">{{
                            countries.join(', ')
                          }}</span>
                        </div>
                      </div>
                      <div>
                        <span class="vacc-cell-link-container">
                          Read
                          <a
                            class="vacc-cell-travel-health"
                            @click="openTravelHealthModal"
                            >Travel Health
                            <i class="material-icons">arrow_forward</i></a
                          >
                        </span>
                      </div>
                    </div>
                    <div v-else>
                      <span>
                        No mandatory vaccinations. Read
                        <a
                          class="vacc-cell-travel-health"
                          @click="openTravelHealthModal"
                          >Travel Health
                          <i class="material-icons">arrow_forward</i></a
                        >
                      </span>
                    </div>
                  </div>
                </div>
              </PassengerAccordion>
            </div>
          </div>
          <TravelHealth ref="TravelHealthRef" />

          <!-- Accordions -->
          <div class="advice-accordions" role="tablist">
            <AdviceAccordion
              title="Passport Advice"
              :isOpen="openAdviceIndex === 1"
              @toggle="toggleAdviceAccordion(1)"
            >
              <p
                class="modal-visa-title"
                v-for="nationality in bookingEssentialInformationVisa.nationalities"
                :key="nationality.description"
              >
                <strong
                  ><span>PASSPORT ADVICE FOR</span>{{ ' '
                  }}{{ nationality.description }}</strong
                >
              </p>
              <p
                v-for="message in bookingEssentialInformationVisa.passpmessages"
                :key="message"
              >
                <span v-if="!message.includes('Visa requirements')">{{
                  message
                }}</span>
              </p>
              <ServiceErrorMessage
                v-bind:error-loading-data="errorLoadingData"
              ></ServiceErrorMessage>
            </AdviceAccordion>
            <AdviceAccordion
              title="Additional Entry Information"
              :isOpen="openAdviceIndex === 2"
              @toggle="toggleAdviceAccordion(2)"
            >
              <div
                v-for="message in bookingEssentialInformationVisa.visamessages"
                :key="message"
              >
                <div v-html="message" class="entry-reqs-visa-message" />
              </div>
              <ServiceErrorMessage
                v-bind:error-loading-data="errorLoadingData"
              ></ServiceErrorMessage>
            </AdviceAccordion>
            <AdviceAccordion
              v-if="onlinePreTravelFormInformation.length > 0"
              title="Online Entry Form Information"
              :isOpen="openAdviceIndex === 3"
              @toggle="toggleAdviceAccordion(3)"
              id="onlineEntryFormsAcc"
            >
              <div
                v-for="obj in onlinePreTravelFormInformation"
                :key="obj.country"
                class="accordion-item"
              >
                <p class="online-form-header">{{ obj.country }}</p>
                <div
                  v-for="message in obj.msgs"
                  class="online-form-message"
                  :key="message"
                >
                  {{ message }}
                </div>
              </div>
            </AdviceAccordion>
          </div>
        </div>
        <div v-else>
          <div
            v-if="
              bookingEssentialInformationVisa &&
              bookingEssentialInformationVisa.nationalities &&
              bookingEssentialInformationVisa.nationalities.length
            "
            class="modal-visa-heading text-center"
          >
            <p
              v-for="(
                nationality, i
              ) in bookingEssentialInformationVisa.nationalities"
              :key="i"
            >
              <strong>{{ nationality.description }}</strong>
            </p>
          </div>
          <p
            v-for="(
              vmessage, i
            ) in bookingEssentialInformationVisa.passpmessages"
            :key="i"
          >
            <span v-if="vmessage.includes('Visa requirements')">{{
              vmessage
            }}</span>
          </p>
          <div>
            <p class="modal-visa-heading text-center">
              <strong>VISA(S) REQUIRED</strong>
            </p>
            <div v-if="visaContent.info.length >= 0" class="text-center">
              <p
                v-for="visa in bookingEssentialInformationVisa.visa_requirements"
                :key="visa.description"
              >
                {{ visa.description }}
              </p>
            </div>
            <div
              v-if="
                visaContent.info.length > 0 ||
                (bookingEssentialInformationVisa.visamessages &&
                  bookingEssentialInformationVisa.visamessages.length > 0)
              "
            >
              <p
                class="modal-link text-center"
                v-for="visa in visaContent.info"
                :key="visa.code"
              >
                <a v-if="visa.link === true" :href="visa.url" target="_blank">
                  <img
                    :src="`https://www.trailfinders.com/img/visas/flags/${visa.country.trim().replace(/ /g, '_')}.png`"
                    class="custom-image-visas"
                  />
                  &nbsp;&nbsp;&nbsp;{{ visa.description }}</a
                >
              </p>

              <p class="modal-visa-heading text-center mt-4">
                <strong>ADDITIONAL ENTRY REQUIREMENTS</strong>
              </p>
              <p
                v-for="(
                  message, i
                ) in bookingEssentialInformationVisa.visamessages"
                :key="i"
              >
                <span v-html="message"></span>
              </p>

              <p class="modal-visa-heading text-center mt-4">
                <strong>PASSPORT ADVICE </strong>
              </p>
              <div>
                <p
                  class="modal-visa-title"
                  v-for="nationality in bookingEssentialInformationVisa.nationalities"
                  :key="nationality.description"
                >
                  <strong
                    ><span>PASSPORT ADVICE FOR</span>
                    {{ nationality.description }}</strong
                  >
                </p>
                <p
                  v-for="message in bookingEssentialInformationVisa.passpmessages"
                  :key="message"
                >
                  <span v-if="!message.includes('Visa requirements')">{{
                    message
                  }}</span>
                </p>

                <ServiceErrorMessage
                  v-bind:error-loading-data="errorLoadingData"
                ></ServiceErrorMessage>
              </div>
            </div>
            <ServiceErrorMessage
              v-bind:error-loading-data="errorLoadingData"
            ></ServiceErrorMessage>
          </div>
        </div>

        <div class="entry-reqs-footer vtd-bg-faded vtd-flex-column">
          <small
            >This information was generated at:
            <strong class="return-date">{{ returnDate }}</strong></small
          >
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import dayjs from '@/lib/dayjs'
import { mapState, mapGetters } from 'vuex'
import { getSite } from '@/lib/utilities'
import ServiceErrorMessage from '@/partials/service-error-message'
import TravelHealth from '@/modules/menu-bar/essential-info/travel-health'
import { loadVisaInfo } from '@/modules/menu-bar/essential-info/visa//services'
import AdviceAccordion from '@/modules/menu-bar/essential-info/visa/adviceAccordion.vue'
import PassengerAccordion from '@/modules/menu-bar/essential-info/visa/passengerAccordion.vue'

export default {
  components: {
    AdviceAccordion,
    PassengerAccordion,
    ServiceErrorMessage,
    TravelHealth,
  },
  data() {
    return {
      aggregatedVaccines: {},
      bookingEssentialInformationVisa: { nationalities: [] },
      errorLoadingData: false,
      hasEntryRequirements: false,
      ieBooking: false,
      menuPayload: '',
      onlinePreTravelFormInformation: [],
      openAdviceIndex: null,
      openPassengerIndex: null,
      visaContent: {
        info: [],
        entryRequirements: [],
      },
    }
  },
  computed: {
    ...mapState(['booking']),
    ...mapGetters('booking', ['getCityCodes', 'getBookingNumber']),
    ...mapGetters({ essentialInfoMenu: 'essentialInfoMenu/getMenu' }),
    returnDate: () => dayjs().format('DD-MMM-YYYY HH:mm'),
  },
  methods: {
    async showModal(menuPayload) {
      this.$refs['visa-info-modal-ref'].show()
      this.ieBooking = this.getBookingNumber.startsWith('RA') ? true : false
      if (this.menuPayload !== '') {
        return
      }
      this.menuPayload = menuPayload
      this.bookingEssentialInformationVisa = menuPayload.payload
      const visaPayload = {
        visa: this.bookingEssentialInformationVisa.visa_requirements,
        site: getSite(),
        clients: this.booking.bookingPassengers
          ? this.booking.bookingPassengers.map((passenger) => passenger)
          : [],
        bookno: this.getBookingNumber,
      }
      this.$store.dispatch('spinner/updateLoadingAction', true, { root: true })

      let result = await loadVisaInfo(visaPayload)
      this.$store.dispatch('spinner/updateLoadingAction', false, {
        root: true,
      })

      if (result.error) {
        this.errorLoadingData = true
        return
      }
      if (result.data && result.data.entryRequirements) {
        if (result.data.hasEntryRequirements) {
          this.hasEntryRequirements = true
          this.onlinePreTravelFormInformation = this.aggregatePreTravelForms(
            result.data.entryRequirements,
          )
          this.aggregatedVaccines = this.aggregateVaccinations(
            result.data.entryRequirements,
          )
        }
        this.visaContent = result.data
      } else {
        this.errorLoadingData = true
        return
      }
    },
    displayTfVisaServiceLink(countryCode) {
      return this.visaContent.info.find((obj) => obj.code === countryCode)
    },
    isHomeCountry(countryCode) {
      return countryCode === 'GB' || countryCode === 'IE' || countryCode === 'GS'
    },
    getTfLink(countryCode) {
      return this.visaContent.info.find((item) => item.code === countryCode).url
    },
    getFcoAdviceUrl(visaCountry) {
      const domain = this.ieBooking
        ? 'ireland.ie/en/dfa/overseas-travel/advice'
        : 'gov.uk/foreign-travel-advice'
      const slug =
        !this.ieBooking && visaCountry.country === 'United States of America'
          ? 'usa'
          : visaCountry.country.replace(/ /g, '-').toLowerCase()
      const suffix = this.ieBooking
        ? '#entry-requirements'
        : 'entry-requirements'
      return `https://www.${domain}/${slug}/${suffix}`
    },
    aggregatePreTravelForms(clients) {
      const countryMap = {}

      for (const client of clients) {
        for (const form of client.preTravelForms) {
          const { country, msgs } = form

          if (countryMap[country]) {
            for (const msg of msgs) {
              if (!countryMap[country].msgs.includes(msg)) {
                countryMap[country].msgs.push(msg)
              }
            }
          } else {
            countryMap[country] = { country, msgs: [...msgs] }
          }
        }
      }
      return Object.values(countryMap)
    },
    aggregateVaccinations(clients) {
      const clientMap = {}

      for (const client of clients) {
        const vaccines = {}
        for (const vacc of client.vaccinations) {
          if (!vacc.required) continue
          if (!vaccines[vacc.label]) {
            vaccines[vacc.label] = []
          }
          if (!vaccines[vacc.label].includes(vacc.country)) {
            vaccines[vacc.label].push(vacc.country)
          }
        }
        if (Object.keys(vaccines).length) {
          clientMap[client.cl_id] = { cl_name: client.cl_name, vaccines }
        }
      }
      return clientMap
    },
    closeModal() {
      this.$refs['visa-info-modal-ref'].hide()
    },
    toggleAdviceAccordion(index) {
      this.openAdviceIndex = this.openAdviceIndex === index ? null : index
    },
    togglePassengerAccordion(clientId) {
      this.openPassengerIndex =
        this.openPassengerIndex === clientId ? null : clientId
    },
    openTravelHealthModal() {
      for (const [index, item] of this.essentialInfoMenu.entries()) {
        if (item.partial === 'TravelHealthRef') {
          this.$refs.TravelHealthRef.showModal(this.essentialInfoMenu[index])
          this.$root.$on('travelhealth', () => {
            if (
              this.$refs &&
              this.$refs.TravelHealthRef &&
              this.$refs.TravelHealthRef.showModal
            ) {
              this.$refs.TravelHealthRef.showModal(
                this.essentialInfoMenu[index],
              )
            }
          })
        }
      }
    },
    scrollToOnlineEntryForms() {
      if (this.openAdviceIndex != 3) this.toggleAdviceAccordion(3)
      setTimeout(() => {
        document
          .getElementById('onlineEntryFormsAcc')
          .scrollIntoView({ behavior: 'smooth' })
      }, 200)
    },
  },
}
</script>

<style lang="scss">
@import './style.scss';
</style>
